import {useMemo, useState} from "react";
import {ABACPolicyEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseABACPolicyDataTableFilterProps = Omit<UseDataTableFilterProps<ABACPolicyEntity>, 'filterConfig'>

export function useABACPolicyDataTableFilter(props: UseABACPolicyDataTableFilterProps): UseDataTableFilterResult<ABACPolicyEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [descriptionQuery, setDescriptionQuery] = useState<string | undefined>(undefined);
  const [descriptionDebouncedQuery] = useDebouncedValue(descriptionQuery, 200);

  const filterConfig: DataTableFilterConfig<ABACPolicyEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: ({name}, query) => !name || !name.toLowerCase().includes(query.toLowerCase())
      },
      "description": {
        setQuery: setDescriptionQuery,
        query: descriptionQuery,
        debouncedQuery: descriptionDebouncedQuery,
        filter: ({description}, query) => !description || !description.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    descriptionQuery,
    descriptionDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
